import {type Epic} from 'redux-observable';
import {from, defer, Subject, of, timer, merge, EMPTY} from 'rxjs';
import {map, filter, switchMap, mergeMap, distinctUntilChanged, share} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import {type Action} from 'web-app/util/redux/action-types';
import {type RootState} from 'web-app/react/redux/main-reducer';
import AccountApi from 'web-app/api/account-api';
import {hasValue} from 'web-app/util/typescript';
import {APIErrorCode} from 'web-app/api/types';
import LocalStorage from 'web-app/services/local-storage';
import {EXPECTED_EXPIRATION_TIMESTAMP_KEY, TOKEN_REFRESH_TIMESTAMP_KEY} from 'web-app/util/access-token-helpers';

import * as Helpers from '../helpers';
import * as EpicObservables from './epic-observables';

/**
 * This file is a junction point for the automatic logout feature.
 *
 * The bulk of the logic is implemented in the neighboring file `epic-observables`, which provides
 * functions and observables that require dependencies to be provides, such as a callback to persist
 * the expiration timestamp, or a callback to extend the session.
 *
 * This file imports the contents of said file and provides these dependencies to create a range of Epics
 * that together compose the feature.
 */

/**
 * This epic is the "entry point" for everything. Internally this will be called with an action indicating
 * we've logged in, which will lead to the session being extended, and the success of that will lead to
 * everything else like timers being initiated.
 */
const extendSession: Epic<Action<any>, any, RootState> = (action$, state$) =>
    EpicObservables.extendSession(action$, state$, {handleExtendSession, persistExpirationTimestamp});

const extendSessionTimer: Epic<Action<any>, any, RootState> = (action$, state$) =>
    EpicObservables.extendSessionTimer(action$, state$, {
        handleExtendSession,
        customActivityObservable$: makeIntercomObservable$(),
        persistExpirationTimestamp,
        lastRefreshTimestamp$,
    });

export const sessionExpirationTimer: Epic<Action<any>, any, RootState> = (action$, state$) =>
    EpicObservables.sessionExpirationTimer(action$, state$, {
        expirationTimestamp$,
        /**
         * We calculate the difference between the unix timestamp and now by substracting the two unix timestamps
         * from each other and dividing by 1000 to return the time difference in seconds.
         */
        getDeviceTimeDifference: timestamp => Math.floor((timestamp - Date.now()) / 1000),
    });

const triggerLogoutOnExpire: Epic<Action<any>, any, RootState> = (action$, state$) =>
    EpicObservables.triggerLogoutOnExpire(action$, state$, {});

const logout: Epic<Action<any>, any, RootState> = (action$, state$) =>
    EpicObservables.logout(action$, state$, {handleLogout: () => Helpers.logOut('', undefined, true)});

export default combineEpics(extendSession, extendSessionTimer, sessionExpirationTimer, triggerLogoutOnExpire, logout);

/**
 * Below is an assortment of dependencies for the epics above. These include:
 * - A way to persist timestamps of expiration and last refresh
 * - Observables of said timestamps that update when they change
 * - Callback to extend the session via API
 *
 * - Not strictly necessary for the automatic logout feature to work is an observable that emits when
 *   Intercom is interacted with (See https://github.com/famly/app/pull/11512)
 */

/**
 * We want the current session to be extended if Intercom is being interacted with.
 * This creates an observable that emits when Intercom is opened or closed.
 *
 * This observable implementes the `CustomActivityObservable` type declared in `epic-observables.ts`
 */
const makeIntercomObservable$ = () => {
    // Early exit if Intercom is not loaded
    if (!window.Intercom) {
        return EMPTY;
    }

    // Use Subject to emit when callbacks are invoked
    const subject$ = new Subject<'showing' | 'not-showing'>();
    window.Intercom('onShow', () => subject$.next('showing'));
    window.Intercom('onHide', () => subject$.next('not-showing'));

    // Manipulate subject into timed emits when value is "showing"
    const onShowObservable$ = subject$.pipe(
        // Use SwitchMap to restart inner observable
        switchMap(value =>
            of(value).pipe(
                // Only take when intercom is showing
                filter(value => value === 'showing'),
                mergeMap(() =>
                    merge(
                        // Immediately emit once to trigger activity
                        of(value),
                        // After 60 seconds keep emitting once every minute
                        timer(60000, 60000),
                    ),
                ),
            ),
        ),
        // Map emit to satisfy type
        map(() => ({
            type: 'custom-activity' as const,
            description: 'Intercom is showing',
        })),
    );

    // Emit once when value is "not-showing"
    const onHideObservable$ = subject$.pipe(
        filter(value => value === 'not-showing'),
        map(() => ({
            type: 'custom-activity' as const,
            description: 'Intercom was closed',
        })),
    );

    return merge(onShowObservable$, onHideObservable$);
};

const getExpirationTimestamp = () =>
    LocalStorage.getFromLocalStorage(EXPECTED_EXPIRATION_TIMESTAMP_KEY) as string | undefined;

const getLastRefreshTimestamp = () =>
    LocalStorage.getFromLocalStorage(TOKEN_REFRESH_TIMESTAMP_KEY) as string | undefined;

// General observable pattern to read from any value and only emit when the value changes.
const makeSelfUpdatingObservable$ = <T, U>(getValue: () => T | undefined, transform: (value: T) => U) =>
    defer(() => {
        return timer(0, 1000).pipe(map(getValue), distinctUntilChanged(), filter(hasValue), map(transform), share());
    });

// Observable that emits when local storage flag for expiration timestamp changes
const expirationTimestamp$ = makeSelfUpdatingObservable$(getExpirationTimestamp, timestamp =>
    new Date(timestamp).getTime(),
);

// Observable that emits when local storage flag for last refresh timestamp changes
const lastRefreshTimestamp$ = makeSelfUpdatingObservable$(getLastRefreshTimestamp, timestamp =>
    new Date(timestamp).getTime(),
);

// Persists the timestamp for when the session expires, and saves an estimate of when the sessin was refreshed
const persistExpirationTimestamp = (timestamp: string) => {
    LocalStorage.saveToLocalStorage(EXPECTED_EXPIRATION_TIMESTAMP_KEY, timestamp);
    LocalStorage.saveToLocalStorage(TOKEN_REFRESH_TIMESTAMP_KEY, Date.now());
};

// Callback for calling the API to extend the session.
const handleExtendSession = () =>
    defer(() => from(AccountApi.extendSession({ignoredErrorCodes: [APIErrorCode.NOT_AUTHORIZED]}))).pipe(
        map(v => v.data),
        filter(hasValue),
        map(data => data.me.keepAlive.afterSeconds),
    );
